import React, { useState } from 'react';
import Modal from '../Modal';
import MimaText from '../MimaText';
import FieldRenderer from './FieldRenderer';
import { ReceiptCheckBox as RCB } from '../Receipt';
import styles from '../../assets/styles/EditMode/EditMode.module.scss';
import { MimaButton } from '../Button';
import { BiSolidChevronDownCircle } from 'react-icons/bi';
import { Formik } from 'formik';
import { StyleCatalogController } from '../../controllers';
import constant from '../../utils/constant';
import { MimaToastUtil } from '../Toast';
import useEditMode from './useEditMode';
import { catalogStore } from '../../stores';
import { removeEmptyKeysAndObjects } from '../../utils/utils';

const ConfigModal = ({ closeModal, generalSettings }) => {
	const {
		generateInitialValues,
		// generateValidationSchema,
		convertFormData,
		findImagesToUpload,
		prepareImagesForUpload,
		uploadFiles,
		linkUploadedUrls,
		insertUpdatedImages,
		contentToKeyValueArray,
		keyValueArrayToContent,
		removeValidation,
		updateContentWithValidation,
		removeIdKeys,
	} = useEditMode();

	const initialValues = generateInitialValues(generalSettings.data);
	// const validationSchema = generateValidationSchema(generalSettings.data);

	const [expandedSections, setExpandedSections] = useState({});
	const [loading, setLoading] = useState(false);

	const toggleSection = sectionId => {
		setExpandedSections(prev => ({
			...prev,
			[sectionId]: !prev[sectionId],
		}));
	};

	const updateSettingsInThemeData = (originalObject, editedSettings) => {
		return {
			...originalObject,
			generalSettings: {
				themeName: originalObject?.generalSettings?.themeName,
				data: editedSettings?.sections,
			},
		};
	};

	const { themeData, configThemeData, themeId, setThemeId, setThemeData } =
		catalogStore;

	const onSubmit = async values => {
		setLoading(true);

		const imagesToUpload = findImagesToUpload(values);
		let editedSettings;

		if (imagesToUpload?.length > 0) {
			const preparedFiles = prepareImagesForUpload(imagesToUpload);
			const uploadedUrls = await uploadFiles(preparedFiles);
			const updatedImages = linkUploadedUrls(imagesToUpload, uploadedUrls);
			const updatedValues = insertUpdatedImages(updatedImages, values);
			editedSettings = convertFormData(updatedValues);
		} else {
			editedSettings = convertFormData(values);
		}

		let updatedThemeData;

		if (themeData?.pages?.length) {
			updatedThemeData = updateSettingsInThemeData(themeData, editedSettings);
		} else {
			updatedThemeData = updateSettingsInThemeData(
				configThemeData,
				editedSettings
			);
		}

		const validationRemoved = removeValidation(updatedThemeData);
		const switchedToKeyValueArray = contentToKeyValueArray(validationRemoved);
		const prePayload = removeEmptyKeysAndObjects(switchedToKeyValueArray);

		const payload = {
			config: {
				themeName: prePayload?.generalSettings?.themeName,
				data: prePayload?.generalSettings?.data,
			},
			pages: [...prePayload?.pages],
		};

		const { status, errorMessage, data } = await (themeData?.pages?.length
			? StyleCatalogController.updateThemeData(payload, themeId)
			: StyleCatalogController.createThemeData(payload));

		if (status === constant.Success) {
			const returnedThemeData = { ...data?.themeData };
			setThemeId(returnedThemeData?._id);
			const postPayload = {
				generalSettings: {
					themeName: returnedThemeData?.config?.themeName,
					data: returnedThemeData?.config?.data,
				},
				pages: [...returnedThemeData?.pages],
			};

			const reshapedThemeData = keyValueArrayToContent(postPayload);
			const idRemovedThemeData = removeIdKeys(reshapedThemeData);

			const themeDataWithValidation = updateContentWithValidation(
				idRemovedThemeData,
				configThemeData
			);
			setThemeData(themeDataWithValidation);

			setLoading(false);
			closeModal();

			MimaToastUtil.success({
				message: `${constant.Success}, Reloading page`,
			});

			return setTimeout(() => {
				window.location.reload();
			}, 500);
		}
		setLoading(false);
		return MimaToastUtil.error({
			message: errorMessage,
		});
	};

	return (
		<Modal closeModal={closeModal}>
			<MimaText variant="subtitleBold" mb={3}>
				General Settings
			</MimaText>
			<Formik
				initialValues={initialValues}
				// validationSchema={validationSchema}
				onSubmit={onSubmit}>
				{({
					handleChange,
					handleBlur,
					handleSubmit,
					setFieldValue,
					values,
					errors,
					touched,
				}) => {
					return (
						<>
							<div className={styles.sections}>
								{generalSettings.data.map(section => (
									<div key={section.id}>
										<button
											onClick={e => {
												e.preventDefault();
												toggleSection(section.id);
											}}
											className={`${styles.section__btn} ${expandedSections[section.id] ? styles.section__btn__active2 : ''}`}>
											<span>Edit {section.name}</span>
											<BiSolidChevronDownCircle
												className={`${styles.section__arr} ${expandedSections[section.id] ? styles.section__arr__active : ''}`}
											/>
										</button>
										{expandedSections[section.id] && (
											<div className={styles.section__expanded}>
												<MimaText variant="subtitleBold">
													{section.name}
												</MimaText>

												{!section?.compulsory ? (
													<div className={styles.section__flex}>
														<RCB
															title={`Show ${section?.name}`}
															onChange={() =>
																setFieldValue(`${section.id}.isVisible`, true)
															}
															checked={values[section.id].isVisible === true}
															id={`${section.id}_visible_show`}
															name={`${section.id}.isVisible`}
															value={true}
														/>
														<RCB
															title={`Hide ${section?.name}`}
															onChange={() =>
																setFieldValue(`${section.id}.isVisible`, false)
															}
															checked={values[section.id].isVisible === false}
															id={`${section.id}_visible_hide`}
															name={`${section.id}.isVisible`}
															value={false}
														/>
													</div>
												) : (
													''
												)}

												{section?.defaultContent
													? Object.keys(section.defaultContent)?.map(key => (
															<FieldRenderer
																key={key}
																field={key}
																value={values[section.id][key]}
																validation={section.validation[key]}
																onChange={handleChange}
																onBlur={handleBlur}
																name={`${section.id}.${key}`}
																error={errors[section.id]?.[key]}
																touched={touched[section.id]?.[key]}
																setFieldValue={setFieldValue}
																arrayTitle={section.name}
															/>
														))
													: ''}
											</div>
										)}
									</div>
								))}
								<MimaButton
									title="Save Changes"
									onClick={handleSubmit}
									variant="form"
									mt={2}
									loading={loading}
								/>
							</div>
						</>
					);
				}}
			</Formik>
		</Modal>
	);
};

export default ConfigModal;
